/**
 * Advanced report state: a small external store (useSyncExternalStore) so every interaction - view customisation,
 * saved views CRUD, status changes, history loads - is plain async TypeScript that tests drive without a DOM.
 * All network goes through the injected AdvancedReportApi (BFF in production, fixture mock in tests).
 */
import { useSyncExternalStore } from 'react';
import type {
  AdvancedReport, FindingStatus, FindingStatusChange, ReportFilters, ReportView, ReportViewDefinition,
} from '@qm/contracts/audit-remediation';
import { errorKeyOf, isFlagOff, isNotFound, type AdvancedReportApi } from './api';
import {
  DEFAULT_DEFINITION, definitionOf, reasonValid, sameDefinition, type Column, type Density, type GroupBy, type Loc, type Section, type SortField,
} from './model';

export type Load<T> = { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; kind: 'flag_off' | 'not_found' | 'error' };
export type Result = { ok: true } | { ok: false; errorKey: string };
export interface Notice { tone: 'ok' | 'error'; key: string; params?: Record<string, string> }

export interface ReportUiState {
  report: Load<AdvancedReport>;
  views: Load<ReportView[]>;
  activeViewId: string | null;
  def: ReportViewDefinition;
  density: Density;
  contentLocale: Loc;
  printMode: boolean;
  selectedId: string | null;
  /** last known state version per finding (from PATCH responses), sent as expectedVersion */
  versions: Record<string, number>;
  history: Record<string, Load<FindingStatusChange[]>>;
  busy: string | null;
  notice: Notice | null;
}

const ok: Result = { ok: true };
const failed = (e: unknown): Result => ({ ok: false, errorKey: errorKeyOf(e) });
const loadError = (e: unknown): Load<never> => ({ status: 'error', kind: isFlagOff(e) ? 'flag_off' : isNotFound(e) ? 'not_found' : 'error' });

export class ReportStore {
  private state: ReportUiState;
  private listeners = new Set<() => void>();

  constructor(readonly api: AdvancedReportApi, readonly jobId: string, init: Partial<ReportUiState> = {}) {
    this.state = {
      report: { status: 'loading' }, views: { status: 'loading' }, activeViewId: null, def: DEFAULT_DEFINITION, density: 'comfortable',
      contentLocale: 'en', printMode: false, selectedId: null, versions: {}, history: {}, busy: null, notice: null, ...init,
    };
  }

  /** Preloaded store (SSR tests, previews). */
  static ready(api: AdvancedReportApi, report: AdvancedReport, views: ReportView[] = [], init: Partial<ReportUiState> = {}): ReportStore {
    const s = new ReportStore(api, report.jobId, { report: { status: 'ready', data: report }, views: { status: 'ready', data: views }, ...init });
    if (!init.def && !init.activeViewId) s.applyDefaultView();
    return s;
  }

  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  getSnapshot = () => this.state;
  private set(p: Partial<ReportUiState>) { this.state = { ...this.state, ...p }; for (const l of this.listeners) l(); }

  // ---------- loading ----------
  async load(): Promise<void> {
    this.set({ report: { status: 'loading' } });
    const [report, views] = await Promise.allSettled([this.api.getReport(this.jobId), this.api.listViews()]);
    this.set({
      report: report.status === 'fulfilled' ? { status: 'ready', data: report.value } : loadError(report.reason),
      views: views.status === 'fulfilled' ? { status: 'ready', data: views.value } : loadError(views.reason),
    });
    this.applyDefaultView();
  }

  async reloadReport(): Promise<void> {
    try { this.set({ report: { status: 'ready', data: await this.api.getReport(this.jobId) } }); } catch (e) { this.set({ notice: { tone: 'error', key: errorKeyOf(e) } }); }
  }

  private applyDefaultView() {
    const v = this.state.views.status === 'ready' ? this.state.views.data.find((x) => x.isDefault) : undefined;
    if (v) this.set({ activeViewId: v.id, def: definitionOf(v) });
  }

  // ---------- customisation (local until saved) ----------
  setDef(p: Partial<ReportViewDefinition>) { this.set({ def: { ...this.state.def, ...p } }); }
  setFilter<K extends keyof ReportFilters>(k: K, v: ReportFilters[K]) { this.setDef({ filters: { ...this.state.def.filters, [k]: v } }); }
  toggleFilter(k: Exclude<keyof ReportFilters, 'search'>, value: string) {
    const cur = this.state.def.filters[k] as string[];
    this.setFilter(k, (cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]) as ReportFilters[typeof k]);
  }
  clearFilters() { this.setDef({ filters: DEFAULT_DEFINITION.filters }); }
  setSort(field: SortField, dir?: 'asc' | 'desc') {
    const cur = this.state.def.sort;
    this.setDef({ sort: { field, dir: dir ?? (cur.field === field ? (cur.dir === 'asc' ? 'desc' : 'asc') : field === 'title' ? 'asc' : 'desc') } });
  }
  setGroupBy(groupBy: GroupBy) { this.setDef({ groupBy }); }
  toggleColumn(c: Column) {
    const cur = this.state.def.columns;
    if (cur.includes(c) && cur.length === 1) return; // at least one column (contract min 1)
    this.setDef({ columns: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] });
  }
  toggleSection(s: Section) {
    const cur = this.state.def.hiddenSections;
    this.setDef({ hiddenSections: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] });
  }
  setDensity(density: Density) { this.set({ density }); }
  setContentLocale(contentLocale: Loc) { this.set({ contentLocale }); }
  setPrintMode(printMode: boolean) { this.set({ printMode }); }
  select(selectedId: string | null) { this.set({ selectedId }); if (selectedId) void this.loadHistory(selectedId); }
  dismissNotice() { this.set({ notice: null }); }

  /** The active saved view differs from what is on screen. */
  get dirty(): boolean {
    const v = this.activeView;
    return v ? !sameDefinition(definitionOf(v), this.state.def) : !sameDefinition(DEFAULT_DEFINITION, this.state.def);
  }
  get activeView(): ReportView | undefined {
    return this.state.views.status === 'ready' ? this.state.views.data.find((v) => v.id === this.state.activeViewId) : undefined;
  }

  // ---------- saved views ----------
  applyView(id: string | null) {
    if (id === null) { this.set({ activeViewId: null, def: DEFAULT_DEFINITION }); return; }
    const v = this.state.views.status === 'ready' ? this.state.views.data.find((x) => x.id === id) : undefined;
    if (v) this.set({ activeViewId: v.id, def: definitionOf(v) });
  }

  private viewsList(): ReportView[] { return this.state.views.status === 'ready' ? this.state.views.data : []; }
  private putViews(list: ReportView[]) { this.set({ views: { status: 'ready', data: list } }); }
  private async run(key: string, fn: () => Promise<Notice | null>): Promise<Result> {
    this.set({ busy: key, notice: null });
    try { const n = await fn(); this.set({ busy: null, notice: n }); return ok; } catch (e) {
      const r = failed(e); this.set({ busy: null, notice: { tone: 'error', key: (r as { errorKey: string }).errorKey } }); return r;
    }
  }

  createView(name: string, isDefault = false): Promise<Result> {
    const trimmed = name.trim();
    if (!trimmed) return Promise.resolve({ ok: false, errorKey: 'audit-remediation.report.views.nameRequired' });
    return this.run('view', async () => {
      const v = await this.api.createView({ ...this.state.def, name: trimmed, isDefault });
      this.putViews([...this.viewsList().map((x) => (v.isDefault ? { ...x, isDefault: false } : x)), v]);
      this.set({ activeViewId: v.id });
      return { tone: 'ok', key: 'audit-remediation.report.views.created', params: { name: v.name } };
    });
  }

  /** Save what is on screen into the active view. */
  saveActiveView(): Promise<Result> {
    const v = this.activeView;
    if (!v) return Promise.resolve({ ok: false, errorKey: 'audit-remediation.report.views.noneActive' });
    return this.run('view', async () => {
      const next = await this.api.updateView(v.id, this.state.def);
      this.putViews(this.viewsList().map((x) => (x.id === next.id ? next : x)));
      return { tone: 'ok', key: 'audit-remediation.report.views.saved', params: { name: next.name } };
    });
  }

  renameView(id: string, name: string): Promise<Result> {
    const trimmed = name.trim();
    if (!trimmed) return Promise.resolve({ ok: false, errorKey: 'audit-remediation.report.views.nameRequired' });
    return this.run('view', async () => {
      const next = await this.api.updateView(id, { name: trimmed });
      this.putViews(this.viewsList().map((x) => (x.id === next.id ? next : x)));
      return { tone: 'ok', key: 'audit-remediation.report.views.renamed', params: { name: next.name } };
    });
  }

  setDefaultView(id: string, isDefault = true): Promise<Result> {
    return this.run('view', async () => {
      const next = await this.api.updateView(id, { isDefault });
      this.putViews(this.viewsList().map((x) => (x.id === next.id ? next : next.isDefault ? { ...x, isDefault: false } : x)));
      return { tone: 'ok', key: isDefault ? 'audit-remediation.report.views.defaultSet' : 'audit-remediation.report.views.defaultCleared', params: { name: next.name } };
    });
  }

  deleteView(id: string): Promise<Result> {
    return this.run('view', async () => {
      const name = this.viewsList().find((x) => x.id === id)?.name ?? '';
      await this.api.deleteView(id);
      this.putViews(this.viewsList().filter((x) => x.id !== id));
      if (this.state.activeViewId === id) this.set({ activeViewId: null });
      return { tone: 'ok', key: 'audit-remediation.report.views.deleted', params: { name } };
    });
  }

  // ---------- findings ----------
  async loadHistory(findingId: string): Promise<void> {
    this.set({ history: { ...this.state.history, [findingId]: { status: 'loading' } } });
    try {
      const data = await this.api.getHistory(this.jobId, findingId);
      this.set({ history: { ...this.state.history, [findingId]: { status: 'ready', data } } });
    } catch (e) {
      this.set({ history: { ...this.state.history, [findingId]: loadError(e) } });
    }
  }

  /** PATCH status (validated first: dismiss / accept risk need a reason of 5+ characters). */
  async changeStatus(findingId: string, to: FindingStatus, reason = ''): Promise<Result> {
    if (!reasonValid(to, reason)) return { ok: false, errorKey: 'audit-remediation.errors.reasonRequired' };
    if (to !== 'open' && to !== 'in_progress' && to !== 'dismissed' && to !== 'accepted_risk') return { ok: false, errorKey: 'audit-remediation.errors.transitionNotAllowed' };
    const r0 = this.state.report;
    const expectedVersion = this.state.versions[findingId] ?? (r0.status === 'ready' ? r0.data.findings.find((f) => f.id === findingId)?.stateVersion : undefined);
    return this.run(`status:${findingId}`, async () => {
      const s = await this.api.updateStatus(this.jobId, findingId, {
        status: to, ...(reason.trim() ? { reason: reason.trim() } : {}), ...(expectedVersion !== undefined ? { expectedVersion } : {}),
      });
      const r = this.state.report;
      if (r.status === 'ready') {
        const findings = r.data.findings.map((f) => (f.id === findingId ? { ...f, status: s.status, statusReason: s.reason, statusUpdatedAt: s.updatedAt, stateVersion: s.version } : f));
        this.set({ report: { status: 'ready', data: { ...r.data, findings } } });
      }
      this.set({ versions: { ...this.state.versions, [findingId]: s.version } });
      void this.loadHistory(findingId);
      return { tone: 'ok', key: 'audit-remediation.report.status.changed', params: { status: s.status } };
    });
  }
}

export function useReportState(store: ReportStore): ReportUiState {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
